import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, MapPin, X } from "lucide-react";
import { Locale } from "@/i18n";
import { localePath } from "@/lib/nav";
const showroomImg = "https://media.lacontessina.com/site/src/assets/showroom/hero.webp";
const STORAGE_KEY = "lc_welcome_milan_seen_v1";

export default function WelcomeModal() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language as Locale) || "it";
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        const id = window.setTimeout(() => setOpen(true), 600);
        return () => window.clearTimeout(id);
      }
    } catch {
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const close = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* noop */
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-modal-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-500"
    >
      <button
        type="button"
        aria-label={t("welcome.cta_close")}
        onClick={close}
        className="absolute inset-0 bg-black/55 backdrop-blur-md"
      />
      <div className="relative w-full max-w-[920px] max-h-[92vh] overflow-hidden rounded-[24px] bg-background shadow-2xl grid grid-cols-1 md:grid-cols-2 animate-in zoom-in-95 fade-in duration-500">
        <button
          type="button"
          onClick={close}
          aria-label={t("welcome.cta_close")}
          className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-background/80 backdrop-blur border border-foreground/10 text-foreground/70 hover:text-foreground hover:bg-background flex items-center justify-center transition"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="relative hidden md:block min-h-[420px]">
          <img
            src={showroomImg}
            alt="La Contessina Showroom Milano"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-white/85">
            <MapPin className="h-3.5 w-3.5" />
            Milano
          </div>
        </div>
        <div className="p-8 sm:p-10 md:p-12 flex flex-col justify-center">
          <span className="lc-eyebrow">{t("welcome.kicker")}</span>
          <h2
            id="welcome-modal-title"
            className="mt-5 font-display font-light tracking-[-0.03em] leading-[1.05] text-foreground text-[28px] sm:text-[32px] md:text-[36px]"
          >
            {t("welcome.title_1")}
            <span className="block italic font-serif font-normal">
              {t("welcome.title_2")}
            </span>
          </h2>
          <p className="mt-5 text-[14px] leading-[24px] text-foreground/65">
            {t("welcome.body")}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to={localePath(locale, "showroom")}
              onClick={(e) => {
                e.preventDefault();
                close();
                navigate(localePath(locale, "showroom"));
              }}
              className="lc-btn-solid justify-center"
            >
              {t("welcome.cta_primary")}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <button
              type="button"
              onClick={close}
              className="inline-flex items-center justify-center rounded-md border border-foreground/30 bg-background px-5 py-3 text-[13px] font-medium text-foreground hover:bg-foreground hover:text-background transition"
            >
              {t("welcome.cta_close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}